import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {serializeToolsForAPI} from './aiTools';
import type {LLMTool} from './aiTools';

export type AssistantContextType = {
  instructions: string;
  tools: LLMTool[];
  toolContext: any;
  setInstructions: (instructions: string) => void;
  setTools: (tools: LLMTool[]) => void;
  setToolContext: (toolContext: any) => void;
  serializedTools: ReturnType<typeof serializeToolsForAPI>;
};

const AssistantContext = createContext<AssistantContextType | null>(null);

export type AssistantProviderProps = {
  children: React.ReactNode;
  defaultInstructions?: string;
  defaultTools?: LLMTool[];
};

export const AssistantProvider = ({
  children,
  defaultInstructions = `You are an AI assistant that helps users create and edit educational lessons.`,
  defaultTools = [],
}: AssistantProviderProps) => {
  const [instructions, setInstructions] = useState(defaultInstructions);
  const [tools, setTools] = useState<LLMTool[]>(defaultTools);
  const [toolContext, setToolContext] = useState<any>(null);

  const serializedTools = useMemo(() => serializeToolsForAPI(tools), [tools]);

  const contextValue = useMemo(
    (): AssistantContextType => ({
      instructions,
      tools,
      toolContext,
      setInstructions,
      setTools,
      setToolContext,
      serializedTools,
    }),
    [instructions, tools, toolContext, serializedTools]
  );

  return (
    <AssistantContext.Provider value={contextValue}>
      {children}
    </AssistantContext.Provider>
  );
};

export const useAssistant = () => {
  const context = useContext(AssistantContext);
  if (context === null) {
    throw new Error('useAssistant must be used within an AssistantProvider');
  }
  return context;
};

export const useAssistantInstructions = (instructions?: string) => {
  const {instructions: currentInstructions, setInstructions} = useAssistant();

  useEffect(() => {
    if (instructions !== undefined) {
      setInstructions(instructions);
    }
  }, [instructions, setInstructions]);

  return {
    instructions: currentInstructions,
    setInstructions,
  };
};

export const useAssistantTools = (tools?: LLMTool[], toolContext?: any) => {
  const {
    tools: currentTools,
    setTools,
    toolContext: currentToolContext,
    setToolContext,
    serializedTools,
  } = useAssistant();

  useEffect(() => {
    if (tools !== undefined) {
      setTools(tools);
    }

    if (toolContext !== undefined) {
      setToolContext(toolContext);
    }
  }, []);

  return {
    tools: currentTools,
    setTools,
    toolContext: currentToolContext,
    setToolContext,
    serializedTools,
  };
};
